import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { formatAmount } from 'utils/helper';

const styles = StyleSheet.create({
  page: {
    paddingTop: 24,
    paddingHorizontal: 16,
    // Keep table content clear of the absolute footer (avoids footer wrapping to a ghost page)
    paddingBottom: 36,
    fontSize: 10,
    fontFamily: 'Helvetica',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingBottom: 12,
    alignItems: 'center',
    borderBottom: '1px solid #F5F7F9',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#000',
  },
  greenDot: {
    color: '#89CA45',
  },
  printDate: {
    fontSize: 8,
    color: 'black',
  },
  companyDetails: {
    alignItems: 'flex-start',
    marginBottom: 16,
  },
  companyName: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  companyInfo: {
    fontSize: 8,
    color: '#000',
    marginBottom: 4,
  },
  infoContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  infoSection: {
    width: '48%',
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    marginBottom: 8,
    color: '#000',
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  label: {
    width: 60,
    fontSize: 8,
    color: '#000',
    fontWeight: 400,
  },
  value: {
    fontSize: 8,
    color: '#000',
    textAlign: 'right',
    fontWeight: 500,
  },
  table: {
    marginTop: 0,
  },
  tableHeader: {
    fontSize: 7,
    fontWeight: 'bold',
    flexDirection: 'row',
    backgroundColor: '#F5F7F9',
    padding: '8px',
    borderBottomWidth: 1,
    borderBottomColor: '#F5F5F5',
  },
  tableRow: {
    fontSize: 7,
    fontWeight: 400,
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderBottomWidth: 1,
    borderBottomColor: '#F5F5F5',
    padding: '8px 8px',
  },
  col1: {
    width: '20%',
    maxWidth: '20%',
    minWidth: 0,
    paddingRight: 3,
  },
  col3: {
    width: '14%',
    maxWidth: '14%',
    minWidth: 0,
    paddingRight: 3,
  },
  col4: {
    width: '10%',
    maxWidth: '10%',
    minWidth: 0,
    paddingRight: 3,
  },
  col5: {
    width: '9%',
    maxWidth: '9%',
    minWidth: 0,
    paddingRight: 3,
  },
  col6: {
    width: '8%',
    maxWidth: '8%',
    minWidth: 0,
    paddingRight: 3,
  },
  col7: { width: '5%', maxWidth: '5%', minWidth: 0, paddingRight: 3 },
  col8: { width: '10%', maxWidth: '10%', minWidth: 0, paddingRight: 3 },
  col9: { width: '12%', maxWidth: '12%', minWidth: 0, paddingRight: 3 },
  col10: { width: '12%', maxWidth: '12%', minWidth: 0 },
  cellText: {
    width: '100%',
  },
  // react-pdf does not wrap unbroken IDs; short Text chunks + flexWrap force 2+ lines
  wrapCell: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  wrapChunk: {
    fontSize: 7,
  },
  footer: {
    position: 'absolute',
    bottom: 16,
    left: 24,
    right: 24,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 8,
    color: '#000',
  },
  pageNumber: {
    fontSize: 8,
    color: '#000',
    textAlign: 'right',
  },
  totalAmount: {
    fontSize: 8,
    fontWeight: 600,
    color: '#000',
  },
  total: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#000',
    flexDirection: 'row',
    justifyContent: 'space-between',
    backgroundColor: '#F5F5F5',
    paddingTop: '8px',
    paddingBottom: '8px',
  },
});

const PDF = ({ invoiceDetails, transactions }) => (
  <Document>
    {paginateByHeight(transactions)?.map((pageTransactions, pageIndex, allPages) => (
      <Page key={pageIndex} size={[625, 842]} style={styles.page}>
        <View style={styles.header}>
          <Text style={styles.headerTitle}>
            PostEx<Text style={styles.greenDot}>.</Text>
          </Text>
          <Text style={styles.printDate}>Printed at: {format(new Date(), 'MM/dd/yyyy, hh:mm a')}</Text>
        </View>
        {pageIndex === 0 && (
          <>
            <View style={styles.companyDetails}>
              <Text style={styles.companyName}>{invoiceDetails?.merchant_id?.businessName}</Text>
              <Text style={styles.companyInfo}>{invoiceDetails?.merchant_id?.contactNumber}</Text>
              <Text style={styles.companyInfo}>{invoiceDetails?.merchant_id?.email}</Text>
              <Text style={styles.companyInfo}>{invoiceDetails?.merchant_id?.website}</Text>
              <Text style={styles.companyInfo}>{invoiceDetails?.merchant_id?.address}</Text>
            </View>

            <View style={styles.infoContainer}>
              <View style={styles.infoSection}>
                <Text style={styles.sectionTitle}>Invoice Details</Text>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Invoice Number</Text>
                  <Text style={styles.value}>{invoiceDetails?._id}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Amount</Text>
                  <Text style={styles.value}>PKR {formatAmount(invoiceDetails?.invoice_gross_amount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Status</Text>
                  <Text style={styles.value}>{invoiceDetails?.status}</Text>
                </View>
                {invoiceDetails?.status === 'settled' && (
                  <View style={styles.detailRow}>
                    <Text style={styles.label}>Settlement Date</Text>
                    <Text style={styles.value}>
                      {formatInTimeZone(
                        invoiceDetails?.settlement_date ? invoiceDetails?.settlement_date : invoiceDetails?.updatedAt,
                        'Asia/Karachi',
                        'MMMM d, yyyy, h:mm a'
                      )}
                    </Text>
                  </View>
                )}
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Created Date</Text>
                  <Text style={styles.value}>{formatInTimeZone(invoiceDetails?.createdAt, 'Asia/Karachi', 'MMMM d, yyyy, h:mm a')}</Text>
                </View>
              </View>

              <View style={styles.infoSection}>
                <Text style={styles.sectionTitle}>Invoice Summary</Text>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Transactions</Text>
                  <Text style={styles.value}>{invoiceDetails?.captured_transaction_count}</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.invoice_gross_amount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Charges</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.rate_charged_amount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Region Tax</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.tax_charged_amount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>W.H.T. Sales</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.withHoldingSaleTaxAmount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>W.H.T. Income</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.withHoldingIncomeTaxAmount, 'PKR')}</Text>
                </View>
                <View style={styles.detailRow}>
                  <Text style={styles.label}>Refund</Text>
                  <Text style={styles.value}>{invoiceDetails?.refunded_transaction_count}</Text>
                  <Text style={styles.value}>{formatAmount(invoiceDetails?.refunded_amount, 'PKR')}</Text>
                </View>
                <View
                  style={[
                    styles.detailRow,
                    {
                      borderTopWidth: 1,
                      borderTopColor: '#F5F5F5',
                      paddingTop: 8,
                    },
                  ]}>
                  <Text style={[styles.label, styles.totalAmount]}>Net Amount</Text>
                  <Text style={[styles.value, styles.totalAmount]}>PKR {formatAmount(invoiceDetails?.invoice_net_amount, 'PKR')}</Text>
                </View>
              </View>
            </View>
          </>
        )}

        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <View style={styles.col1}>
              <Text style={styles.cellText}>Order / Payment ID</Text>
            </View>
            <View style={styles.col3}>
              <Text style={styles.cellText}>Time</Text>
            </View>
            <View style={styles.col4}>
              <Text style={styles.cellText}>Status</Text>
            </View>
            <View style={styles.col5}>
              <Text style={[styles.cellText, { textAlign: 'right' }]}>Amount</Text>
            </View>
            <View style={styles.col6}>
              <Text style={[styles.cellText, { textAlign: 'right' }]}>Charges</Text>
            </View>
            <View style={styles.col7}>
              <Text style={[styles.cellText, { textAlign: 'right' }]}>Tax</Text>
            </View>
            <View style={styles.col8}>
              <Text style={[styles.cellText, { textAlign: 'right' }]}>W.H.T. Sales</Text>
            </View>
            <View style={styles.col9}>
              <Text style={[styles.cellText, { textAlign: 'right' }]}>W.H.T. Income</Text>
            </View>
            <View style={styles.col10}>
              <Text style={[styles.cellText, { textAlign: 'right' }]}>Merchant Amount</Text>
            </View>
          </View>

          {pageTransactions?.map((transaction, index) => (
            <View key={index} style={styles.tableRow}>
              <View style={[styles.col1, styles.wrapCell]}>
                {splitForWrap(transaction?.order_id)?.map((part, partIndex) => (
                  <Text key={partIndex} style={styles.wrapChunk}>
                    {part}
                  </Text>
                ))}
              </View>
              <View style={styles.col3}>
                <Text style={styles.cellText}>{formatInTimeZone(transaction?.createdAt, 'Asia/Karachi', 'dd/MM/yyyy, h:mm a')}</Text>
              </View>
              <View style={styles.col4}>
                <Text style={styles.cellText}>{transaction?.status}</Text>
              </View>
              <View style={styles.col5}>
                <Text style={[styles.cellText, { textAlign: 'right' }]}>{formatAmount(transaction?.amount, 'PKR')}</Text>
              </View>
              <View style={styles.col6}>
                <Text style={[styles.cellText, { textAlign: 'right' }]}>{formatAmount(transaction?.invoiceStats?.feeChargeAmount, 'PKR')}</Text>
              </View>
              <View style={styles.col7}>
                <Text style={[styles.cellText, { textAlign: 'right' }]}>{formatAmount(transaction?.invoiceStats?.regionTaxAmount, 'PKR')}</Text>
              </View>
              <View style={styles.col8}>
                <Text style={[styles.cellText, { textAlign: 'right' }]}>{formatAmount(transaction?.invoiceStats?.withHoldingSaleTaxAmount, 'PKR')}</Text>
              </View>
              <View style={styles.col9}>
                <Text style={[styles.cellText, { textAlign: 'right' }]}>{formatAmount(transaction?.invoiceStats?.withHoldingIncomeTaxAmount, 'PKR')}</Text>
              </View>
              <View style={styles.col10}>
                <Text style={[styles.cellText, { textAlign: 'right' }]}>{formatAmount(transaction?.invoiceStats?.netAmount, 'PKR')}</Text>
              </View>
            </View>
          ))}

          {pageIndex === allPages?.length - 1 && (
            <View style={styles.total}>
              <Text style={styles.col1}></Text>
              <Text style={styles.col1}>Total</Text>
              <Text style={[{ textAlign: 'right', paddingRight: 8 }]}>PKR {formatAmount(invoiceDetails?.invoice_net_amount, 'PKR')}</Text>
            </View>
          )}
        </View>

        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>*Note: For any queries, please contact us at support@postexglobal.com</Text>
          <Text style={styles.pageNumber}>
            page {pageIndex + 1} of {allPages?.length}
          </Text>
        </View>
      </Page>
    ))}
  </Document>
);

// flexWrap chunk size (layout). Height estimate uses wider chars/line — chunks sit side-by-side.
const ORDER_ID_CHUNK_SIZE = 12;
const ORDER_ID_CHARS_PER_LINE = 26;

// A4 layout reserves (pt) — keep a small safety buffer so pages fill without overflowing
const PAGE_HEIGHT = 841.89;
const PAGE_PADDING = 60; // top 24 + bottom 36
const HEADER_HEIGHT = 50;
const TABLE_HEADER_HEIGHT = 34;
const PAGE1_DETAILS_RESERVE = 245;
const TOTAL_ROW_HEIGHT = 32;
const ROW_BASE_HEIGHT = 26;
const ROW_LINE_EXTRA = 10;
const SAFETY_MARGIN = 10;

// Split IDs into short chunks so flexWrap can keep them inside the column
const splitForWrap = (value, chunkSize = ORDER_ID_CHUNK_SIZE) => {
  const str = String(value ?? '');
  if (!str) return [''];
  const parts = [];
  for (let i = 0; i < str?.length; i += chunkSize) {
    parts?.push(str?.slice(i, i + chunkSize));
  }
  return parts;
};

const estimateOrderIdLines = (orderId) => Math?.max(1, Math?.ceil(String(orderId ?? '')?.length / ORDER_ID_CHARS_PER_LINE));

const estimateRowHeight = (transaction) => ROW_BASE_HEIGHT + (estimateOrderIdLines(transaction?.order_id) - 1) * ROW_LINE_EXTRA;

const getAvailableTableHeight = (pageIndex, reserveTotal) => {
  let available = PAGE_HEIGHT - PAGE_PADDING - HEADER_HEIGHT - TABLE_HEADER_HEIGHT - SAFETY_MARGIN;

  if (pageIndex === 0) {
    available -= PAGE1_DETAILS_RESERVE;
  }
  if (reserveTotal) {
    available -= TOTAL_ROW_HEIGHT;
  }
  return Math.max(available, ROW_BASE_HEIGHT);
};

const pageUsedHeight = (rows) => rows?.reduce((sum, txn) => sum + estimateRowHeight(txn), 0);

// Prefer filling earlier pages: merge a sparse last page back into the previous one when it fits with Total
const consolidatePages = (pages) => {
  if (pages?.length < 2) return pages;
  const out = pages?.map((page) => [...page]);

  while (out?.length >= 2) {
    const lastIdx = out?.length - 1;
    const prevIdx = lastIdx - 1;
    const merged = [...out[prevIdx], ...out[lastIdx]];
    const mergedFits = pageUsedHeight(merged) <= getAvailableTableHeight(prevIdx, true);

    if (!mergedFits) break;

    out[prevIdx] = merged;
    out.pop();
  }

  return out;
};

const paginateByHeight = (transactions) => {
  const list = Array.isArray(transactions) ? transactions : [];
  if (!list?.length) return [[]];

  const pages = [];
  let index = 0;
  let pageIndex = 0;

  while (index < list?.length) {
    const pageRows = [];
    let used = 0;
    const pageBudget = getAvailableTableHeight(pageIndex, false);

    while (index < list?.length) {
      const rowHeight = estimateRowHeight(list[index]);
      const isLastRow = index === list?.length - 1;
      const restHeight = list?.slice(index)?.reduce((sum, txn) => sum + estimateRowHeight(txn), 0);
      // Reserve Total when this is the last row, or when all remaining rows fit with it
      const reserveTotal = isLastRow || used + restHeight + TOTAL_ROW_HEIGHT <= pageBudget;
      const available = reserveTotal ? pageBudget - TOTAL_ROW_HEIGHT : pageBudget;

      if (pageRows?.length > 0 && used + rowHeight > available) {
        break;
      }

      pageRows.push(list[index]);
      used += rowHeight;
      index += 1;

      // Always allow at least one row even if slightly over estimate
      if (pageRows?.length === 1 && used > available) {
        break;
      }
    }

    pages.push(pageRows);
    pageIndex += 1;
  }

  return consolidatePages(pages);
};

export default PDF;
